import React, { useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import StoreLocationMap from './StoreLocationMap/StoreLocationMap';
import StoreLocationTextCard from './StoreLocationTextCard/StoreLocationTextCard';
import StoreHoursRow from '../StoreHoursRow/StoreHoursRow';
import { getStoreTimeZone } from '../../StoreHoursPage/StoreHoursForm/storeHoursUtils';
import RatingSummary from '../ReviewsTab/RatingSummary/RatingSummary';
import { FormattedMessage } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { isTooManyEmailVerificationRequestsError } from '../../../util/errors';
import { resolveStoreAddress } from '../../../components/Map/locationMapPresentation';
import countryList from 'country-list';
import queryString from 'query-string';
import css from './Sidebar.module.css';
import { parse } from '../../../util/urlHelpers';

const USE_PUBLICDATA_REVIEW_SUMMARY_UI =
  process.env.REACT_APP_USE_PUBLICDATA_REVIEW_SUMMARY_UI !== 'false';

const Sidebar = ({
  user,
  storeLocation,
  isCurrentUser = false,
  currentUser,
  onSendVerificationEmail,
  sendVerificationEmailInProgress,
  sendVerificationEmailError,
  reviewsReceived = [],
  totalReviewsReceived = 0,
  achievementReviews = [],
  history,
  location,
  reviewsReceivedError,
}) => {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const userPublicData = user?.attributes?.profile?.publicData || {};
  const summaryStarCounts = userPublicData.starCounts;
  const summaryAverageRating = Number(userPublicData.rating);
  const summaryReviewCount = Number(userPublicData.reviews);
  const achievementRatings = (achievementReviews || [])
    .map(r => Number(r?.attributes?.rating))
    .filter(r => Number.isFinite(r) && r >= 1 && r <= 5);
  const achievementReviewCount = achievementRatings.length;
  const achievementRatingSum = achievementRatings.reduce((sum, rating) => sum + rating, 0);
  const achievementDistribution = achievementRatings.reduce(
    (acc, rating) => {
      const rounded = Math.round(rating);
      if (rounded >= 1 && rounded <= 5) {
        acc[rounded] = (acc[rounded] || 0) + 1;
      }
      return acc;
    },
    { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  );
  const hasPublicDataSummary =
    USE_PUBLICDATA_REVIEW_SUMMARY_UI &&
    Number.isFinite(summaryAverageRating) &&
    summaryAverageRating >= 0 &&
    Number.isFinite(summaryReviewCount) &&
    summaryReviewCount > 0 &&
    summaryStarCounts &&
    typeof summaryStarCounts === 'object';

  const publicDataSummary = hasPublicDataSummary
    ? (() => {
        const baseReviewCount = Math.floor(summaryReviewCount);
        const mergedReviewCount = baseReviewCount + achievementReviewCount;
        const baseRatingSum = Number(summaryAverageRating) * baseReviewCount;
        const mergedRatingSum = baseRatingSum + achievementRatingSum;
        const mergedAverage =
          mergedReviewCount > 0 ? Number((mergedRatingSum / mergedReviewCount).toFixed(1)) : 0;
        return {
          averageRating: mergedAverage,
          reviewCount: mergedReviewCount,
          starCounts: {
            1: Number(summaryStarCounts?.[1] ?? summaryStarCounts?.['1'] ?? 0) + achievementDistribution[1],
            2: Number(summaryStarCounts?.[2] ?? summaryStarCounts?.['2'] ?? 0) + achievementDistribution[2],
            3: Number(summaryStarCounts?.[3] ?? summaryStarCounts?.['3'] ?? 0) + achievementDistribution[3],
            4: Number(summaryStarCounts?.[4] ?? summaryStarCounts?.['4'] ?? 0) + achievementDistribution[4],
            5: Number(summaryStarCounts?.[5] ?? summaryStarCounts?.['5'] ?? 0) + achievementDistribution[5],
          },
        };
      })()
    : null;

  const sidebarSummaryReviews = useMemo(
    () => [...(reviewsReceived || []), ...(achievementReviews || [])],
    [reviewsReceived, achievementReviews]
  );
  const totalSummaryReviewsCount = hasPublicDataSummary
    ? publicDataSummary.reviewCount
    : Number(totalReviewsReceived || 0) + achievementReviewCount;

  const allProviderReviewsLoaded =
    !totalReviewsReceived ||
    totalReviewsReceived <= 0 ||
    (reviewsReceived || []).length >= totalReviewsReceived;

  const distributionIncomplete =
    !hasPublicDataSummary &&
    totalSummaryReviewsCount > 0 &&
    !reviewsReceivedError &&
    !allProviderReviewsLoaded;

  const handleReviewsSummaryClick = useCallback(
    e => {
      e.preventDefault();
      if (!history || !location) return;
      const parsed = parse(location.search || '');
      const next = { ...parsed, tab: 'reviews' };
      const qs = queryString.stringify(next);
      history.push({
        pathname: location.pathname,
        search: qs ? `?${qs}` : '?tab=reviews',
        ...(location.hash ? { hash: location.hash } : {}),
      });
    },
    [history, location]
  );

  const hasMapGeolocation =
    !!storeLocation && storeLocation.lat != null && storeLocation.lng != null;
  const storeTimeZone = useMemo(() => getStoreTimeZone(storeLocation), [
    storeLocation?.lat,
    storeLocation?.lng,
  ]);

  const { mapAddressForLocation, sidebarCountryDisplayName } = useMemo(() => {
    const pd = user?.attributes?.profile?.publicData || {};
    const sa =
      typeof pd.storeAddress === 'object' && pd.storeAddress ? pd.storeAddress : null;
    const code = (sa?.country || pd.country || '').trim();
    const countryName = code ? countryList.getName(code) : null;
    return {
      // This used to read publicData.storeAddress only, so a store that marked its
      // address "same as personal" (no separate storeAddress record is written in that
      // case - see StoreBasicInformationPage.duck.js) or that only ever filled in a
      // profile address resolved to '', which made shouldShowExactLocation fail and hid
      // the address entirely even for owners who had opted in. resolveStoreAddress adds
      // the profile-address fallback.
      mapAddressForLocation: resolveStoreAddress(pd),
      sidebarCountryDisplayName: countryName,
    };
  }, [user]);

  const showReviewEmailCta =
    isCurrentUser && currentUser?.attributes?.emailVerified !== true;
  const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
    sendVerificationEmailError
  )
    ? 'AuthenticationPage.resendFailedTooManyRequests'
    : 'AuthenticationPage.resendFailed';

  return (
    <aside className={css.sidebar}>
      <div className={css.desktopScrollArea}>
        <div className={css.sidebarScrollContainer}>
          <div className={css.sidebarScrollArea}>
            {hasMapGeolocation ? (
              <StoreLocationMap
                geolocation={storeLocation}
                address={mapAddressForLocation || ''}
                showAddressInStore={
                  user?.attributes?.profile?.publicData?.showAddressInStore === true
                }
              />
            ) : (
              <StoreLocationTextCard countryLabel={sidebarCountryDisplayName} />
            )}
            {/* Store Hours: desktop only (1024px+) - mobile/tablet shows this in the
                Header instead, directly below the location/map line. */}
            <div className={css.storeHoursRowWrapper}>
              <StoreHoursRow storeHours={userPublicData.storeHours} timeZone={storeTimeZone} />
            </div>
            {isDesktop && totalSummaryReviewsCount > 0 ? (
              <div className={css.reviewSummaryWrapper}>
                <RatingSummary
                  reviews={sidebarSummaryReviews}
                  totalReviewsCount={totalSummaryReviewsCount}
                  summaryData={publicDataSummary}
                  onReviewsClick={
                    history && location ? handleReviewsSummaryClick : undefined
                  }
                  distributionIncomplete={distributionIncomplete}
                />
              </div>
            ) : null}
            {isDesktop && totalSummaryReviewsCount === 0 ? (
              <div className={css.reviewPlaceholderWrapper}>
                <h3 className={css.reviewPlaceholderTitle}>
                  <FormattedMessage
                    id="StoreProfilePage.Sidebar.reviewsSectionTitle"
                    defaultMessage="Reviews"
                  />
                </h3>
                {isCurrentUser ? (
                  <>
                    <p className={css.reviewPlaceholderText}>
                      <FormattedMessage
                        id="StoreProfilePage.Sidebar.reviewsPlaceholderOwner"
                        defaultMessage="Your review summary will appear here once you receive reviews."
                      />
                    </p>
                    {showReviewEmailCta ? (
                      <>
                        <p className={css.reviewPlaceholderHint}>
                          <FormattedMessage
                            id="StoreProfilePage.Sidebar.reviewsPlaceholderVerifyHint"
                            defaultMessage="Verify your email to get your first verification review."
                          />
                        </p>
                        {sendVerificationEmailError ? (
                          <p className={css.reviewPlaceholderError} role="alert">
                            <FormattedMessage id={resendErrorTranslationId} />
                          </p>
                        ) : null}
                        {typeof onSendVerificationEmail === 'function' ? (
                          <button
                            type="button"
                            className={css.reviewPlaceholderButton}
                            onClick={onSendVerificationEmail}
                            disabled={!!sendVerificationEmailInProgress}
                          >
                            {sendVerificationEmailInProgress ? (
                              <FormattedMessage
                                id="StoreProfilePage.Sidebar.verifyEmailSending"
                                defaultMessage="Sending…"
                              />
                            ) : (
                              <FormattedMessage
                                id="StoreProfilePage.Sidebar.verifyEmailButton"
                                defaultMessage="Verify your email"
                              />
                            )}
                          </button>
                        ) : null}
                      </>
                    ) : null}
                  </>
                ) : (
                  <p className={css.reviewPlaceholderText}>
                    <FormattedMessage
                      id="StoreProfilePage.Sidebar.reviewsPlaceholderVisitor"
                      defaultMessage="No reviews yet."
                    />
                  </p>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </aside>
  );
};

Sidebar.propTypes = {
  user: PropTypes.object.isRequired,
  storeLocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  isCurrentUser: PropTypes.bool,
  currentUser: PropTypes.object,
  onSendVerificationEmail: PropTypes.func,
  sendVerificationEmailInProgress: PropTypes.bool,
  sendVerificationEmailError: PropTypes.object,
  reviewsReceived: PropTypes.arrayOf(PropTypes.object),
  totalReviewsReceived: PropTypes.number,
  achievementReviews: PropTypes.arrayOf(PropTypes.object),
  reviewsReceivedError: propTypes.error,
  history: PropTypes.shape({ push: PropTypes.func }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
    hash: PropTypes.string,
  }),
};

export default React.memo(Sidebar);
